import type { World } from '@/ecs/World';
import type { Entity } from '@/ecs/Entity';
import type { AxialCoord } from '@/types/hex.types';
import type { EventBus } from '@/core/EventBus';
import type { HexGrid } from '@/hex/HexGrid';
import type { HexRenderer } from '@/hex/HexRenderer';
import type { UnitFactory } from '@/units/UnitFactory';
import type { UnitRenderer } from '@/units/UnitRenderer';
import type { MergeSystem } from '@/merge/MergeSystem';
import type { NetworkManager } from '@/network/NetworkManager';
import { Position } from '@/ecs/Component';
import { Owner, UnitMesh, UnitData } from '@/ecs/components/UnitComponents';
import { coordToKey, coordsEqual } from '@/hex/HexCoord';
import { PlayerSide } from '@/types/hex.types';
import { GameEvents } from '@/core/EventBus';
import { getMaxUnitsForRound } from '@/utils/constants';
import { ShopEvents } from '@/shop/ShopSystem';

export interface ShopSlotSelectedEvent {
  slotIndex: number;
  unitType: string;
  cost: number;
}

/**
 * Placement events
 */
export const PlacementEvents = {
  UNIT_SELECTED: 'placement:unitSelected',
  UNIT_DESELECTED: 'placement:unitDeselected',
  UNIT_PLACED: 'placement:unitPlaced',
  UNIT_SWAPPED: 'placement:unitSwapped',
  INVALID_PLACEMENT: 'placement:invalid',
} as const;

export interface UnitSelectedEvent {
  entityId: string;
  playerId: number;
}

export interface UnitPlacedEvent {
  entityId: string;
  coord: AxialCoord;
}

/**
 * Manages unit placement during preparation phase
 */
export class PlacementSystem {
  private world: World;
  private eventBus: EventBus;
  private hexGrid: HexGrid;
  private hexRenderer: HexRenderer;
  private unitFactory: UnitFactory;
  private unitRenderer: UnitRenderer;

  private selectedEntity: Entity | null = null;
  private highlightedTiles: Set<string> = new Set();
  private isEnabled = false;
  private activePlayerId = 1;
  private pendingUnitType: string | null = null;
  private pendingShopSlot: { slotIndex: number; unitType: string; cost: number } | null = null;
  private currentRound = 1;

  // Callback for completing shop purchases
  private onShopPurchase: ((slotIndex: number) => boolean) | null = null;

  // Merge system reference (set after construction to avoid circular deps)
  private mergeSystem: MergeSystem | null = null;

  // Network manager for multiplayer sync
  private networkManager: NetworkManager | null = null;

  constructor(
    world: World,
    eventBus: EventBus,
    hexGrid: HexGrid,
    hexRenderer: HexRenderer,
    unitFactory: UnitFactory,
    unitRenderer: UnitRenderer
  ) {
    this.world = world;
    this.eventBus = eventBus;
    this.hexGrid = hexGrid;
    this.hexRenderer = hexRenderer;
    this.unitFactory = unitFactory;
    this.unitRenderer = unitRenderer;

    this.setupEventListeners();
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    this.eventBus.on<{ coord: AxialCoord }>(GameEvents.HEX_CLICKED, (data) => {
      if (this.isEnabled) {
        this.handleHexClick(data.coord);
      }
    });

    // Listen for hex unhovered to restore placement highlights
    this.eventBus.on<{ coord: AxialCoord }>(GameEvents.HEX_UNHOVERED, (data) => {
      // If this hex is in our highlighted tiles set, restore placement highlight
      const key = coordToKey(data.coord);
      if (this.highlightedTiles.has(key)) {
        this.hexRenderer.highlightForPlacement(data.coord);
      }
    });

    // Listen for shop slot selection
    this.eventBus.on<ShopSlotSelectedEvent>(
      ShopEvents.SLOT_SELECTED,
      (data) => {
        this.setPendingShopSlot(data);
      }
    );

    // Clear pending shop slot when deselected
    this.eventBus.on(ShopEvents.SLOT_DESELECTED, () => {
      this.clearPendingShopSlot();
    });
  }

  /**
   * Set callback for completing shop purchases
   */
  setShopPurchaseCallback(callback: (slotIndex: number) => boolean): void {
    this.onShopPurchase = callback;
  }

  /**
   * Set merge system reference
   */
  setMergeSystem(mergeSystem: MergeSystem): void {
    this.mergeSystem = mergeSystem;
  }

  /**
   * Set network manager for multiplayer sync
   */
  setNetworkManager(networkManager: NetworkManager): void {
    this.networkManager = networkManager;
  }

  /**
   * Send unit placement to server (for multiplayer)
   */
  private notifyNetworkPlacement(unitType: string, coord: AxialCoord, starLevel: number): void {
    if (this.networkManager?.isInGame()) {
      this.networkManager.placeUnit(unitType, coord, starLevel);
    }
  }

  /**
   * Send unit removal to server (for multiplayer)
   */
  private notifyNetworkRemoval(coord: AxialCoord): void {
    if (this.networkManager?.isInGame()) {
      this.networkManager.removeUnit(coord);
    }
  }

  /**
   * Set pending shop slot for placement
   */
  setPendingShopSlot(data: ShopSlotSelectedEvent): void {
    this.clearSelection();
    this.clearPendingUnit();
    this.pendingShopSlot = data;
    this.highlightValidPlacementTiles();
    this.highlightMergeableUnits(data.unitType);
  }

  /**
   * Clear pending shop slot
   */
  clearPendingShopSlot(): void {
    this.pendingShopSlot = null;
    this.clearHighlights();
    this.unitRenderer.clearAllMergeHighlights();
  }

  /**
   * Highlight units that can be merged with the selected unit type
   */
  private highlightMergeableUnits(unitType: string): void {
    const playerSide = this.activePlayerId === 1 ? PlayerSide.Player1 : PlayerSide.Player2;
    const playerTiles = this.hexGrid.getPlayerTiles(playerSide);

    for (const tile of playerTiles) {
      if (!tile.occupant) continue;

      const entity = this.world.getEntity(tile.occupant);
      if (!entity) continue;

      const unitData = entity.getComponent(UnitData);
      const unitMesh = entity.getComponent(UnitMesh);
      const owner = entity.getComponent(Owner);

      // Check if this unit matches and can be upgraded
      if (
        unitData &&
        unitMesh &&
        owner &&
        owner.playerId === this.activePlayerId &&
        unitData.unitType === unitType &&
        unitData.starLevel < 4 // Can still upgrade
      ) {
        this.unitRenderer.setMergeHighlight(unitMesh.meshId, true);
      }
    }
  }

  /**
   * Enable placement mode
   */
  enable(playerId: number = 1, round: number = 1): void {
    this.isEnabled = true;
    this.activePlayerId = playerId;
    this.currentRound = round;
    this.clearSelection();
  }

  /**
   * Set the current round (affects max unit limits)
   */
  setCurrentRound(round: number): void {
    this.currentRound = round;
  }

  /**
   * Get max units allowed for current round
   */
  getMaxUnitsForRound(): number {
    return getMaxUnitsForRound(this.currentRound);
  }

  /**
   * Disable placement mode
   */
  disable(): void {
    this.isEnabled = false;
    this.clearSelection();
    this.clearPendingUnit();
    this.clearPendingShopSlot();
    // Clear all placement highlights to restore tiles to original green
    this.hexRenderer.clearAllPlacementHighlights();
  }

  /**
   * Set pending unit type to spawn on next valid click
   */
  setPendingUnit(unitType: string): void {
    // Clear any existing unit selection first
    this.clearSelection();
    this.pendingUnitType = unitType;
    // Highlight valid placement tiles
    this.highlightValidPlacementTiles();
  }

  /**
   * Clear pending unit type
   */
  clearPendingUnit(): void {
    this.pendingUnitType = null;
    this.clearHighlights();
  }

  /**
   * Get pending unit type
   */
  getPendingUnitType(): string | null {
    return this.pendingUnitType;
  }

  /**
   * Set active player for placement
   */
  setActivePlayer(playerId: number): void {
    this.activePlayerId = playerId;
    this.clearSelection();
  }

  /**
   * Handle hex click
   */
  private handleHexClick(coord: AxialCoord): void {
    const tile = this.hexGrid.getTile(coord);
    if (!tile) return;

    // If we have a pending shop slot, try to buy and place (includes merge)
    if (this.pendingShopSlot) {
      this.tryBuyAndPlaceUnit(coord);
      return;
    }

    // If we have a pending unit type to spawn, try to spawn it
    if (this.pendingUnitType) {
      this.trySpawnUnit(coord);
      return;
    }

    // Check if clicking on a unit to select it
    if (tile.occupant) {
      const entity = this.world.getEntity(tile.occupant);
      if (entity) {
        const owner = entity.getComponent(Owner);
        if (owner && owner.playerId === this.activePlayerId) {
          // Select own unit
          this.selectUnit(entity);
          return;
        }
      }
    }

    // If we have a selected unit, try to place it
    if (this.selectedEntity) {
      this.tryPlaceUnit(coord);
    }
  }

  /**
   * Try to buy from shop and place unit at the given coordinate
   */
  private tryBuyAndPlaceUnit(coord: AxialCoord): boolean {
    if (!this.pendingShopSlot) return false;

    const tile = this.hexGrid.getTile(coord);
    if (!tile) return false;

    // Check if it's player's territory and empty
    if (!this.isValidPlacementTile(coord, this.activePlayerId)) {
      this.eventBus.emit(PlacementEvents.INVALID_PLACEMENT, {
        reason: 'Invalid territory',
        coord,
      });
      return false;
    }

    // Save values before purchase (callback may clear pendingShopSlot via events)
    const { slotIndex, unitType } = this.pendingShopSlot;

    // Check if tile is occupied - might be a merge opportunity
    if (tile.occupant) {
      const occupantEntity = this.world.getEntity(tile.occupant);
      if (occupantEntity) {
        const occupantData = occupantEntity.getComponent(UnitData);
        const occupantOwner = occupantEntity.getComponent(Owner);

        // If clicking on own unit of different type, cancel shop and select unit
        if (
          occupantOwner &&
          occupantOwner.playerId === this.activePlayerId &&
          occupantData &&
          occupantData.unitType !== unitType
        ) {
          this.clearPendingShopSlot();
          this.selectUnit(occupantEntity);
          return false;
        }

        // Check if we can merge: same type, same owner, not at max star
        if (
          this.mergeSystem &&
          occupantData &&
          occupantOwner &&
          occupantData.unitType === unitType &&
          occupantOwner.playerId === this.activePlayerId &&
          occupantData.starLevel < 4 // Can still upgrade
        ) {
          // Try to purchase first
          if (this.onShopPurchase) {
            const purchased = this.onShopPurchase(slotIndex);
            if (!purchased) {
              this.clearPendingShopSlot();
              return false;
            }
          }

          // Upgrade the existing unit directly
          const upgradedEntity = this.mergeSystem.upgradeUnit(occupantEntity);
          if (upgradedEntity) {
            const newData = upgradedEntity.getComponent(UnitData);
            this.eventBus.emit(PlacementEvents.UNIT_PLACED, {
              entityId: upgradedEntity.id,
              coord,
            } as UnitPlacedEvent);

            // Notify server of upgraded unit
            if (newData) {
              this.notifyNetworkPlacement(newData.unitType, coord, newData.starLevel);
            }

            this.clearPendingShopSlot();
            return true;
          }
          return false;
        }
      }

      // Not a valid merge - tile is occupied
      this.eventBus.emit(PlacementEvents.INVALID_PLACEMENT, {
        reason: 'Tile is occupied',
        coord,
      });
      return false;
    }

    // Check if player can place more units (only for new units, not merges)
    if (!this.canPlaceMoreUnits(this.activePlayerId)) {
      this.eventBus.emit(PlacementEvents.INVALID_PLACEMENT, {
        reason: 'Max team size reached',
        coord,
      });
      return false;
    }

    // Try to complete the shop purchase via callback
    if (this.onShopPurchase) {
      const purchased = this.onShopPurchase(slotIndex);
      if (!purchased) {
        // Purchase failed (not enough gold, already purchased, etc.)
        this.clearPendingShopSlot();
        return false;
      }
    }

    // Create the unit at the clicked position
    const entity = this.unitFactory.createUnit(
      unitType,
      this.activePlayerId,
      coord,
      1 // Star level 1
    );

    if (entity) {
      this.eventBus.emit(PlacementEvents.UNIT_PLACED, {
        entityId: entity.id,
        coord,
      } as UnitPlacedEvent);

      // Notify server of placement
      this.notifyNetworkPlacement(unitType, coord, 1);

      // Clear pending shop slot (may already be cleared by events)
      this.clearPendingShopSlot();
      return true;
    }

    return false;
  }

  /**
   * Try to spawn a new unit at the given coordinate
   */
  private trySpawnUnit(coord: AxialCoord): boolean {
    if (!this.pendingUnitType) return false;

    const tile = this.hexGrid.getTile(coord);
    if (!tile) return false;

    // Check if it's player's territory and empty
    if (!this.isValidPlacementTile(coord, this.activePlayerId)) {
      this.eventBus.emit(PlacementEvents.INVALID_PLACEMENT, {
        reason: 'Invalid territory',
        coord,
      });
      return false;
    }

    // Check if tile is occupied
    if (tile.occupant) {
      const occupantEntity = this.world.getEntity(tile.occupant);
      if (occupantEntity) {
        const occupantOwner = occupantEntity.getComponent(Owner);
        // If clicking on own unit, cancel pending spawn and select unit
        if (occupantOwner && occupantOwner.playerId === this.activePlayerId) {
          this.clearPendingUnit();
          this.selectUnit(occupantEntity);
          return false;
        }
      }

      this.eventBus.emit(PlacementEvents.INVALID_PLACEMENT, {
        reason: 'Tile is occupied',
        coord,
      });
      return false;
    }

    // Check if player can place more units
    if (!this.canPlaceMoreUnits(this.activePlayerId)) {
      this.eventBus.emit(PlacementEvents.INVALID_PLACEMENT, {
        reason: 'Max team size reached',
        coord,
      });
      return false;
    }

    // Create the unit
    const entity = this.unitFactory.createUnit(
      this.pendingUnitType,
      this.activePlayerId,
      coord,
      1 // Star level 1
    );

    if (entity) {
      this.eventBus.emit(PlacementEvents.UNIT_PLACED, {
        entityId: entity.id,
        coord,
      } as UnitPlacedEvent);

      // Notify server of placement
      this.notifyNetworkPlacement(this.pendingUnitType!, coord, 1);

      // Clear pending unit type after successful placement
      this.clearPendingUnit();
      return true;
    }

    return false;
  }

  /**
   * Select a unit for movement
   */
  selectUnit(entity: Entity): void {
    // Deselect previous
    if (this.selectedEntity) {
      this.deselectUnit();
    }

    this.selectedEntity = entity;

    // Highlight the unit
    const mesh = entity.getComponent(UnitMesh);
    if (mesh) {
      this.unitRenderer.highlightUnit(mesh.meshId, true);
    }

    // Highlight valid placement tiles
    this.highlightValidTiles();

    const owner = entity.getComponent(Owner);
    this.eventBus.emit(PlacementEvents.UNIT_SELECTED, {
      entityId: entity.id,
      playerId: owner?.playerId ?? 0,
    } as UnitSelectedEvent);
  }

  /**
   * Deselect current unit
   */
  deselectUnit(): void {
    if (!this.selectedEntity) return;

    // Remove highlight from unit
    const mesh = this.selectedEntity.getComponent(UnitMesh);
    if (mesh) {
      this.unitRenderer.highlightUnit(mesh.meshId, false);
    }

    // Clear tile highlights
    this.clearHighlights();

    const entityId = this.selectedEntity.id;
    this.selectedEntity = null;

    this.eventBus.emit(PlacementEvents.UNIT_DESELECTED, { entityId });
  }

  /**
   * Clear selection
   */
  clearSelection(): void {
    this.deselectUnit();
  }

  /**
   * Try to place the selected unit at a coordinate
   */
  private tryPlaceUnit(coord: AxialCoord): boolean {
    if (!this.selectedEntity) return false;

    const tile = this.hexGrid.getTile(coord);
    if (!tile) return false;

    const owner = this.selectedEntity.getComponent(Owner);
    if (!owner) return false;

    // Check if it's player's territory
    if (!this.isValidPlacementTile(coord, owner.playerId)) {
      this.eventBus.emit(PlacementEvents.INVALID_PLACEMENT, {
        reason: 'Invalid territory',
        coord,
      });
      return false;
    }

    // Check if tile is occupied
    if (tile.occupant && tile.occupant !== this.selectedEntity.id) {
      // Swap with existing unit
      const otherEntity = this.world.getEntity(tile.occupant);
      if (otherEntity) {
        const otherOwner = otherEntity.getComponent(Owner);
        if (otherOwner && otherOwner.playerId === owner.playerId) {
          return this.swapUnits(this.selectedEntity, otherEntity);
        }
      }
      return false;
    }

    // Move unit to new position
    const currentPos = this.selectedEntity.getComponent(Position);
    if (currentPos && coordsEqual({ q: currentPos.q, r: currentPos.r }, coord)) {
      // Same position, just deselect
      this.deselectUnit();
      return true;
    }

    // Get unit info before move for network sync
    const unitData = this.selectedEntity.getComponent(UnitData);
    const oldPos = currentPos ? { q: currentPos.q, r: currentPos.r } : null;

    const success = this.unitFactory.moveUnit(this.selectedEntity, coord);
    if (success) {
      this.eventBus.emit(PlacementEvents.UNIT_PLACED, {
        entityId: this.selectedEntity.id,
        coord,
      } as UnitPlacedEvent);

      // Notify server of move (remove from old, place at new)
      if (unitData && oldPos) {
        this.notifyNetworkRemoval(oldPos);
        this.notifyNetworkPlacement(unitData.unitType, coord, unitData.starLevel);
      }

      this.deselectUnit();
    }

    return success;
  }

  /**
   * Swap two units' positions
   */
  private swapUnits(entity1: Entity, entity2: Entity): boolean {
    const success = this.unitFactory.swapUnits(entity1, entity2);

    if (success) {
      const pos1 = entity1.getComponent(Position);
      const pos2 = entity2.getComponent(Position);
      const data1 = entity1.getComponent(UnitData);
      const data2 = entity2.getComponent(UnitData);

      this.eventBus.emit(PlacementEvents.UNIT_SWAPPED, {
        entity1Id: entity1.id,
        entity2Id: entity2.id,
        entity1Coord: pos1 ? { q: pos1.q, r: pos1.r } : null,
        entity2Coord: pos2 ? { q: pos2.q, r: pos2.r } : null,
      });

      // Notify server of both new positions
      if (pos1 && data1) {
        this.notifyNetworkPlacement(data1.unitType, { q: pos1.q, r: pos1.r }, data1.starLevel);
      }
      if (pos2 && data2) {
        this.notifyNetworkPlacement(data2.unitType, { q: pos2.q, r: pos2.r }, data2.starLevel);
      }

      this.deselectUnit();
    }

    return success;
  }

  /**
   * Check if a tile is valid for placement
   */
  isValidPlacementTile(coord: AxialCoord, playerId: number): boolean {
    const tile = this.hexGrid.getTile(coord);
    if (!tile) return false;

    // Must be player's territory
    const expectedOwner = playerId === 1 ? PlayerSide.Player1 : PlayerSide.Player2;
    return tile.owner === expectedOwner;
  }

  /**
   * Highlight all valid placement tiles
   */
  private highlightValidTiles(): void {
    if (!this.selectedEntity) return;

    const owner = this.selectedEntity.getComponent(Owner);
    if (!owner) return;

    const playerTiles = this.hexGrid.getPlayerTiles(
      owner.playerId === 1 ? PlayerSide.Player1 : PlayerSide.Player2
    );

    for (const tile of playerTiles) {
      // Highlight all player tiles (including occupied for swapping)
      this.hexRenderer.highlightForPlacement(tile.coord);
      this.highlightedTiles.add(coordToKey(tile.coord));
    }
  }

  /**
   * Highlight valid placement tiles for spawning new units
   */
  private highlightValidPlacementTiles(): void {
    const emptyTiles = this.getValidPlacementTiles(this.activePlayerId);

    for (const coord of emptyTiles) {
      this.hexRenderer.highlightForPlacement(coord);
      this.highlightedTiles.add(coordToKey(coord));
    }
  }

  /**
   * Clear all tile highlights
   */
  private clearHighlights(): void {
    for (const key of this.highlightedTiles) {
      const [q, r] = key.split(',').map(Number);
      this.hexRenderer.unhighlightHex({ q, r });
    }
    this.highlightedTiles.clear();
  }

  /**
   * Get selected entity
   */
  getSelectedEntity(): Entity | null {
    return this.selectedEntity;
  }

  /**
   * Get unit count for a player
   */
  getUnitCount(playerId: number): number {
    const playerSide = playerId === 1 ? PlayerSide.Player1 : PlayerSide.Player2;
    const playerTiles = this.hexGrid.getPlayerTiles(playerSide);

    let count = 0;
    for (const tile of playerTiles) {
      if (tile.occupant) count++;
    }
    return count;
  }

  /**
   * Check if player can place more units (based on current round limit)
   */
  canPlaceMoreUnits(playerId: number): boolean {
    const maxUnits = getMaxUnitsForRound(this.currentRound);
    return this.getUnitCount(playerId) < maxUnits;
  }

  /**
   * Get remaining unit slots for a player
   */
  getRemainingSlots(playerId: number): number {
    const maxUnits = getMaxUnitsForRound(this.currentRound);
    return Math.max(0, maxUnits - this.getUnitCount(playerId));
  }

  /**
   * Get valid placement tiles for a player
   */
  getValidPlacementTiles(playerId: number): AxialCoord[] {
    const playerSide = playerId === 1 ? PlayerSide.Player1 : PlayerSide.Player2;
    const emptyTiles = this.hexGrid.getEmptyPlayerTiles(playerSide);
    return emptyTiles.map((tile) => tile.coord);
  }

  /**
   * Clean up
   */
  dispose(): void {
    this.clearSelection();
    this.isEnabled = false;
  }
}
